/**
 * Track recorder — an always-armed state machine fed by the position service
 * (spec 03). No record button; trips log themselves. Pure and framework-free so
 * it can be driven by fixture fix-streams in tests.
 *
 *   idle       —(SOG ≥ 2.0 kn sustained 30 s)→   recording
 *   recording  —(SOG < 1.0 kn sustained 10 min)→  finalize → idle
 *   recording  —(no fixes > 2 min)→               gap event, keep recording
 *   finalize:  discard if distance < 0.5 nm OR duration < 5 min
 *
 * Asymmetric thresholds (hysteresis) stop a lock queue or trolling from
 * splitting one trip into ten.
 */

import { haversineNm, haversineMeters } from './geo.js';
import { shouldKeepPoint, type TrackPoint, type DecimationCandidate } from './polyline.js';

export interface RecorderConfig {
  startSogKn: number;
  startSustainMs: number;
  stopSogKn: number;
  stopSustainMs: number;
  gapMs: number;
  minDistanceNm: number;
  minDurationMs: number;
}

export const DEFAULT_RECORDER_CONFIG: RecorderConfig = {
  startSogKn: 2.0,
  startSustainMs: 30_000,
  stopSogKn: 1.0,
  stopSustainMs: 600_000,
  gapMs: 120_000,
  minDistanceNm: 0.5,
  minDurationMs: 300_000,
};

export interface RecorderSample {
  lat: number;
  lon: number;
  sogKn: number | null;
  cogDeg: number | null;
  ts: number;
}

export interface FinalizedTrack {
  startedAt: number;
  endedAt: number;
  distanceNm: number;
  durationS: number;
  avgKn: number;
  maxKn: number;
  points: TrackPoint[];
  pointCount: number;
}

export type RecorderEvent =
  | { type: 'started'; at: number }
  | { type: 'point'; point: TrackPoint }
  | { type: 'gap'; at: number }
  | { type: 'finalized'; track: FinalizedTrack | null };

type State = 'idle' | 'recording';

export interface TripSnapshot {
  startedAt: number;
  distanceNm: number;
  pointCount: number;
  points: TrackPoint[];
}

export class Recorder {
  private readonly cfg: RecorderConfig;
  private state: State = 'idle';

  // idle → recording candidate
  private startCandidateTs: number | null = null;
  private pending: RecorderSample[] = [];

  // recording data
  private points: TrackPoint[] = [];
  private lastKept: DecimationCandidate | null = null;
  private startedAt = 0;
  private maxKn = 0;
  private stopCandidateTs: number | null = null;

  private lastFixTs: number | null = null;

  constructor(config: RecorderConfig = DEFAULT_RECORDER_CONFIG) {
    this.cfg = config;
  }

  get isRecording(): boolean {
    return this.state === 'recording';
  }

  /** Live trip distance (nm) for the trip instrument; 0 when idle. */
  get tripDistanceNm(): number {
    return this.state === 'recording' ? tripDistance(this.points) : 0;
  }

  get tripPointCount(): number {
    return this.points.length;
  }

  /** For crash-safety flush of the liveTrack record. */
  snapshot(): TripSnapshot | null {
    if (this.state !== 'recording') return null;
    return {
      startedAt: this.startedAt,
      distanceNm: tripDistance(this.points),
      pointCount: this.points.length,
      points: this.points.slice(),
    };
  }

  push(sample: RecorderSample): RecorderEvent[] {
    const events = this.state === 'idle' ? this.pushIdle(sample) : this.pushRecording(sample);
    this.lastFixTs = sample.ts;
    return events;
  }

  /** Manual stop (chip tap) — force a finalize attempt. */
  stop(): RecorderEvent[] {
    if (this.state !== 'recording') return [];
    return [this.finalize(this.lastFixTs ?? this.startedAt)];
  }

  /**
   * Rebuild recording state from a persisted liveTrack (crash recovery, spec 03).
   * The next push() continues the same trip; stop() finalizes it.
   */
  resume(snapshot: { startedAt: number; points: TrackPoint[]; maxKn?: number }): void {
    this.reset();
    this.state = 'recording';
    this.startedAt = snapshot.startedAt;
    this.points = snapshot.points.slice();
    const last = this.points[this.points.length - 1];
    this.lastKept = last ? { lat: last.lat, lon: last.lon, ts: last.ts, cogDeg: null } : null;
    this.lastFixTs = last ? last.ts : null;
    this.maxKn =
      snapshot.maxKn ?? this.points.reduce((m, p) => Math.max(m, p.sogKn ?? 0), 0);
  }

  reset(): void {
    this.state = 'idle';
    this.startCandidateTs = null;
    this.pending = [];
    this.points = [];
    this.lastKept = null;
    this.startedAt = 0;
    this.maxKn = 0;
    this.stopCandidateTs = null;
    this.lastFixTs = null;
  }

  private pushIdle(sample: RecorderSample): RecorderEvent[] {
    const sog = sample.sogKn ?? 0;
    if (sog >= this.cfg.startSogKn) {
      if (this.startCandidateTs == null) this.startCandidateTs = sample.ts;
      this.pending.push(sample);
      if (sample.ts - this.startCandidateTs >= this.cfg.startSustainMs) {
        return this.beginRecording();
      }
    } else {
      this.startCandidateTs = null;
      this.pending = [];
    }
    return [];
  }

  private beginRecording(): RecorderEvent[] {
    const seed = this.pending;
    this.state = 'recording';
    this.startedAt = seed[0]!.ts;
    this.points = [];
    this.lastKept = null;
    this.maxKn = 0;
    this.stopCandidateTs = null;

    const events: RecorderEvent[] = [{ type: 'started', at: this.startedAt }];
    // Replay the acceleration phase we buffered, decimated.
    for (const s of seed) {
      const pt = this.consider(s);
      if (pt) events.push({ type: 'point', point: pt });
    }
    this.startCandidateTs = null;
    this.pending = [];
    return events;
  }

  private pushRecording(sample: RecorderSample): RecorderEvent[] {
    const events: RecorderEvent[] = [];

    if (this.lastFixTs != null && sample.ts - this.lastFixTs > this.cfg.gapMs) {
      events.push({ type: 'gap', at: sample.ts });
      // A gap is usually the screen sleeping, not a stop — don't count it toward
      // the low-speed finalize timer.
      this.stopCandidateTs = null;
    }

    const pt = this.consider(sample);
    if (pt) events.push({ type: 'point', point: pt });

    const sog = sample.sogKn ?? 0;
    if (sog < this.cfg.stopSogKn) {
      if (this.stopCandidateTs == null) this.stopCandidateTs = sample.ts;
      else if (sample.ts - this.stopCandidateTs >= this.cfg.stopSustainMs) {
        events.push(this.finalize(this.stopCandidateTs));
      }
    } else {
      this.stopCandidateTs = null;
    }

    return events;
  }

  /** Track max speed and, if it passes decimation, append the point. */
  private consider(sample: RecorderSample): TrackPoint | null {
    const sog = sample.sogKn ?? 0;
    if (sog > this.maxKn) this.maxKn = sog;

    const candidate: DecimationCandidate = {
      lat: sample.lat,
      lon: sample.lon,
      ts: sample.ts,
      cogDeg: sample.cogDeg,
    };
    const distM = this.lastKept
      ? haversineMeters(this.lastKept, candidate)
      : 0;
    if (!shouldKeepPoint(this.lastKept, candidate, distM)) return null;

    const point: TrackPoint = {
      lat: sample.lat,
      lon: sample.lon,
      ts: sample.ts,
      sogKn: sample.sogKn,
    };
    this.points.push(point);
    this.lastKept = candidate;
    return point;
  }

  private finalize(endedAt: number): RecorderEvent {
    // Trim points recorded during the low-speed tail so duration reflects the
    // moment motion stopped, not the 10-min confirmation window.
    const kept = this.points.filter((p) => p.ts <= endedAt);
    const maxKn = this.maxKn;
    this.reset();

    if (kept.length < 2) return { type: 'finalized', track: null };

    const startedAt = kept[0]!.ts;
    const realEnd = kept[kept.length - 1]!.ts;
    const distanceNm = tripDistance(kept);
    const durationMs = realEnd - startedAt;

    if (distanceNm < this.cfg.minDistanceNm || durationMs < this.cfg.minDurationMs) {
      return { type: 'finalized', track: null };
    }

    const durationS = Math.round(durationMs / 1000);
    const avgKn = durationS > 0 ? distanceNm / (durationS / 3600) : 0;
    return {
      type: 'finalized',
      track: {
        startedAt,
        endedAt: realEnd,
        distanceNm,
        durationS,
        avgKn,
        maxKn,
        points: kept,
        pointCount: kept.length,
      },
    };
  }
}

function tripDistance(points: TrackPoint[]): number {
  let sum = 0;
  for (let i = 1; i < points.length; i++) {
    sum += haversineNm(points[i - 1]!, points[i]!);
  }
  return sum;
}
